/**
 * DirectorOnboarding – Tour guiado para directores de club la primera vez que ingresan.
 * Persiste el estado en localStorage para no mostrarse de nuevo.
 */

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  Users,
  Award,
  Calendar,
  FileText,
  Settings,
  LayoutDashboard,
  Share2,
  ChevronRight,
  ChevronLeft,
  X,
  CheckCircle,
  Sparkles,
} from "lucide-react";

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface Step {
  icon: React.ReactNode;
  color: string; // clases de gradiente para el icono
  title: string;
  description: string;
  tip?: string;
}

// ─── Datos de los pasos ───────────────────────────────────────────────────────

const STEPS: Step[] = [
  {
    icon: <Sparkles className="w-8 h-8 text-white" />,
    color: "from-primary-500 to-accent-500",
    title: "¡Bienvenido a Insignia!",
    description:
      "Eres el director de tu club. Desde aquí puedes gestionar todo: miembros, eventos, asistencia, finanzas y progreso.",
    tip: "Este tour tarda menos de 2 minutos.",
  },
  {
    icon: <LayoutDashboard className="w-8 h-8 text-white" />,
    color: "from-blue-500 to-blue-700",
    title: "Dashboard – Inicio",
    description:
      "El panel principal te muestra un resumen en tiempo real: miembros activos, especialidades del periodo, eventos publicados, balance financiero y asistencia promedio.",
    tip: "Siempre empieza aquí para tener una visión general de tu club.",
  },
  {
    icon: <Share2 className="w-8 h-8 text-white" />,
    color: "from-accent-500 to-accent-700",
    title: "Código de Invitación",
    description:
      "Tu club tiene un código único. Compártelo con nuevos miembros para que soliciten unirse. Tú apruebas o rechazas cada solicitud.",
    tip: "Puedes copiar el código con un clic desde el banner del dashboard.",
  },
  {
    icon: <Users className="w-8 h-8 text-white" />,
    color: "from-indigo-500 to-indigo-700",
    title: "Miembros",
    description:
      "Aquí verás el listado de todos tus acampantes y líderes. Puedes aprobar solicitudes de ingreso, registrar datos, y hacer seguimiento de cada miembro.",
    tip: "Los miembros se clasifican automáticamente en sección (Aventureros, Conquistadores, Guías Mayores) según su edad.",
  },
  {
    icon: <Award className="w-8 h-8 text-white" />,
    color: "from-amber-500 to-orange-600",
    title: "Formación y Progreso",
    description:
      "Registra clases, especialidades y logros alcanzados por cada miembro durante el periodo activo.",
    tip: "Usa 'Registrar Logro' en las acciones rápidas para agregar una especialidad completada.",
  },
  {
    icon: <Calendar className="w-8 h-8 text-white" />,
    color: "from-purple-500 to-purple-700",
    title: "Eventos y Campamentos",
    description:
      "Inscribe a tus miembros en campamentos, convocatorias de asociación, cultos especiales y actividades del club. Tú controlas quiénes participan en cada evento.",
    tip: "Desde cada evento puedes inscribir miembros, registrar asistencia y llevar el control de participación.",
  },
  {
    icon: <FileText className="w-8 h-8 text-white" />,
    color: "from-teal-500 to-teal-700",
    title: "Reportes",
    description:
      "Genera reportes de asistencia, avance de especialidades y estado financiero del club, exportables en PDF.",
    tip: "Útil para presentar en reuniones de asociación.",
  },
  {
    icon: <Settings className="w-8 h-8 text-white" />,
    color: "from-gray-500 to-gray-700",
    title: "Configuración del Club",
    description:
      "Personaliza tu club: nombre, logo, secciones activas (Aventureros, Conquistadores, Guías Mayores) y más datos generales.",
    tip: "Empieza por aquí si quieres agregar el logo de tu club.",
  },
  {
    icon: <CheckCircle className="w-8 h-8 text-white" />,
    color: "from-green-500 to-emerald-600",
    title: "¡Listo para empezar!",
    description:
      "Ya conoces las secciones principales. Tu primer paso: agrega miembros a tu club. ¡Mucho éxito como director!",
  },
];

// ─── Constante de localStorage ────────────────────────────────────────────────

const LS_KEY = "insignia_director_onboarded";

export function useDirectorOnboarding(userId: string | undefined) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const done = localStorage.getItem(`${LS_KEY}_${userId}`);
    if (!done) setShow(true);
  }, [userId]);

  const complete = () => {
    if (userId) localStorage.setItem(`${LS_KEY}_${userId}`, "1");
    setShow(false);
  };

  return { show, complete };
}

// ─── Componente principal ─────────────────────────────────────────────────────

interface DirectorOnboardingProps {
  onClose: () => void;
}

export function DirectorOnboarding({ onClose }: DirectorOnboardingProps) {
  const [step, setStep] = useState(0);
  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const next = () => (isLast ? onClose() : setStep((s) => s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));

  // Cerrar con Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Barra de progreso
  const progress = ((step + 1) / STEPS.length) * 100;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Tour de bienvenida"
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Card */}
      <div className="relative z-10 w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-scale-in">
        {/* Barra de progreso superior */}
        <div className="h-1.5 bg-gray-100">
          <div
            className="h-full bg-gradient-to-r from-primary-500 to-accent-500 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Botón cerrar */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors"
          aria-label="Cerrar tour"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Contenido */}
        <div className="p-8 text-center">
          {/* Icono */}
          <div
            className={`w-20 h-20 bg-gradient-to-br ${current.color} rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg`}
          >
            {current.icon}
          </div>

          {/* Numeración */}
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
            Paso {step + 1} de {STEPS.length}
          </p>

          {/* Título */}
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            {current.title}
          </h2>

          {/* Descripción */}
          <p className="text-gray-600 leading-relaxed text-sm">
            {current.description}
          </p>

          {/* Tip */}
          {current.tip && (
            <div className="mt-4 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-left">
              <p className="text-xs text-blue-700 font-medium">
                💡 {current.tip}
              </p>
            </div>
          )}
        </div>

        {/* Dots de navegación */}
        <div className="flex justify-center gap-1.5 pb-2">
          {STEPS.map((_, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              className={`rounded-full transition-all duration-300 ${
                i === step
                  ? "w-5 h-2 bg-primary-600"
                  : "w-2 h-2 bg-gray-200 hover:bg-gray-300"
              }`}
              aria-label={`Ir al paso ${i + 1}`}
            />
          ))}
        </div>

        {/* Botones de navegación */}
        <div className="flex gap-3 px-8 pb-8 mt-2">
          {step > 0 && (
            <button
              onClick={prev}
              className="flex items-center gap-1 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Atrás
            </button>
          )}
          <button
            onClick={next}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-600 to-accent-600 hover:from-primary-700 hover:to-accent-700 text-white rounded-xl text-sm font-semibold transition-all shadow-md shadow-primary-500/25"
          >
            {isLast ? (
              <>
                <CheckCircle className="w-4 h-4" />
                ¡Empezar!
              </>
            ) : (
              <>
                Siguiente
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
